/**
 * User-friendly error message mapping.
 * Converts technical error messages into user-friendly ones.
 */

interface ErrorMapping {
  pattern: RegExp | string
  message: string
}

// Common technical error patterns mapped to user-friendly messages
const ERROR_MAPPINGS: ErrorMapping[] = [
  // Database errors
  { pattern: /connection.*refused|ECONNREFUSED/i, message: 'Unable to connect to the server. Please check your internet connection and try again.' },
  { pattern: /timeout|ETIMEDOUT/i, message: 'The request took too long. Please try again.' },
  { pattern: /database.*does not exist/i, message: 'Database connection error. Please contact support.' },
  { pattern: /relation.*does not exist/i, message: 'Database error. Please try again or contact support.' },
  
  // Authentication errors
  { pattern: /invalid.*token|token.*expired|token.*invalid/i, message: 'Your session has expired. Please log in again.' },
  { pattern: /could not validate credentials/i, message: 'Invalid credentials. Please check your email and password.' },
  { pattern: /incorrect.*email.*or.*password/i, message: 'Invalid email or password. Please try again.' },
  { pattern: /account.*locked/i, message: 'Your account has been temporarily locked due to too many failed login attempts. Please try again later.' },
  { pattern: /user.*not.*found/i, message: 'No account found with this email address.' },
  { pattern: /email.*already.*registered|email.*exists/i, message: 'An account with this email already exists. Please log in instead.' },
  { pattern: /username.*already.*taken|username.*exists/i, message: 'This username is already taken. Please choose another one.' },
  
  // Password errors
  { pattern: /password.*must.*contain/i, message: 'Password does not meet requirements. Please ensure it has at least 8 characters, including uppercase, lowercase, and a number.' },
  { pattern: /password.*too.*short/i, message: 'Password must be at least 8 characters long.' },
  { pattern: /password.*too.*weak/i, message: 'Password is too weak. Please use a stronger password.' },
  
  // File upload errors
  { pattern: /file.*too.*large|request.*too.*large/i, message: 'File is too large. Please upload a smaller file.' },
  { pattern: /file.*type.*not.*allowed|invalid.*file.*type/i, message: 'This file type is not supported. Please upload a different file type.' },
  { pattern: /file.*not.*found|file.*does.*not.*exist/i, message: 'File not found. It may have been deleted.' },
  { pattern: /upload.*failed/i, message: 'File upload failed. Please try again.' },
  
  // LaTeX compilation errors
  { pattern: /compilation.*failed|latex.*error/i, message: 'LaTeX compilation failed. Please check your document for errors.' },
  { pattern: /missing.*begin|missing.*end/i, message: 'LaTeX syntax error: Missing begin or end statement.' },
  { pattern: /undefined.*control.*sequence/i, message: 'LaTeX error: Unknown command. Please check your syntax.' },
  
  // Network errors
  { pattern: /network.*error|network.*request.*failed/i, message: 'Network error. Please check your internet connection and try again.' },
  { pattern: /failed.*to.*fetch/i, message: 'Unable to reach the server. Please check your connection.' },
  { pattern: /CORS|cross.*origin/i, message: 'Connection error. Please refresh the page.' },
  
  // Rate limiting
  { pattern: /rate.*limit|too.*many.*requests/i, message: 'Too many requests. Please wait a moment and try again.' },
  
  // Generic server errors
  { pattern: /internal.*server.*error|500/i, message: 'Server error. Please try again later or contact support if the problem persists.' },
  { pattern: /service.*unavailable|503/i, message: 'Service temporarily unavailable. Please try again in a moment.' },
  { pattern: /bad.*gateway|502/i, message: 'Server error. Please try again later.' },
  
  // Permission errors
  { pattern: /permission.*denied|access.*denied/i, message: 'You do not have permission to perform this action.' },
  { pattern: /forbidden|403/i, message: 'Access denied. You do not have permission to access this resource.' },
  
  // Not found errors
  { pattern: /not.*found|404/i, message: 'The requested resource was not found.' },
  { pattern: /document.*not.*found/i, message: 'Document not found. It may have been deleted.' },
  
  // Validation errors (already formatted, but catch any remaining technical terms)
  { pattern: /validation.*error/i, message: 'Please check your input and try again.' },
  { pattern: /invalid.*input/i, message: 'Invalid input. Please check your data and try again.' },
  
  // Generic fallbacks
  { pattern: /error|failed/i, message: 'An error occurred. Please try again.' },
]

/**
 * Convert a technical error message to a user-friendly one.
 * 
 * @param errorMessage - The technical error message
 * @param statusCode - Optional HTTP status code
 * @returns User-friendly error message
 */
export function getUserFriendlyError(errorMessage: string | undefined | null, statusCode?: number): string {
  if (!errorMessage) {
    return 'An unexpected error occurred. Please try again.'
  }

  const message = String(errorMessage).trim()

  // Check status code first for common HTTP errors
  if (statusCode) {
    switch (statusCode) {
      case 400:
        return 'Invalid request. Please check your input and try again.'
      case 401:
        return 'Please log in to continue.'
      case 403:
        return 'You do not have permission to perform this action.'
      case 404:
        return 'The requested resource was not found.'
      case 409:
        return 'A conflict occurred. The resource may have been modified by another user.'
      case 413:
        return 'The file or request is too large. Please try a smaller size.'
      case 422:
        // Validation errors are usually already formatted, but ensure they're friendly
        return message
      case 429:
        return 'Too many requests. Please wait a moment and try again.'
      case 500:
        return 'Server error. Please try again later or contact support.'
      case 502:
        return 'Server error. Please try again later.'
      case 503:
        return 'Service temporarily unavailable. Please try again in a moment.'
      case 504:
        return 'Request timeout. Please try again.'
    }
  }

  // Try to match against known error patterns
  for (const mapping of ERROR_MAPPINGS) {
    const pattern = typeof mapping.pattern === 'string' 
      ? new RegExp(mapping.pattern, 'i') 
      : mapping.pattern
    
    if (pattern.test(message)) {
      return mapping.message
    }
  }

  // If no match found, return the original message (it might already be user-friendly)
  // But clean it up a bit
  return message
    .replace(/^Error:\s*/i, '')
    .replace(/^Exception:\s*/i, '')
    .replace(/^Value error,\s*/i, '')
    .trim()
    || 'An unexpected error occurred. Please try again.'
}

/**
 * Format validation errors from FastAPI/Pydantic.
 * 
 * @param errors - Array of validation error objects
 * @returns Formatted error message
 */
export function formatValidationErrors(errors: any[]): string {
  if (!Array.isArray(errors) || errors.length === 0) {
    return 'Validation error. Please check your input.'
  }

  try {
    return errors
      .map((err: any) => {
        // Handle different error formats
        if (typeof err === 'string') {
          return err
        }
        
        if (typeof err !== 'object' || err === null) {
          return 'Invalid input'
        }
        
        const field = (err.loc && Array.isArray(err.loc) && err.loc.length > 1)
          ? err.loc[err.loc.length - 1] 
          : (err.field || 'field')
        
        let msg = String(err.msg || err.message || 'Invalid value')
        // Remove technical prefixes
        msg = msg.replace(/^Value error,\s*/i, '').trim()
        msg = msg.replace(/^Type error,\s*/i, '').trim()
        // Capitalize first letter
        if (msg.length > 0) {
          msg = msg.charAt(0).toUpperCase() + msg.slice(1)
        }
        
        // Make field names more user-friendly
        const friendlyField = String(field)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase())
        
        return `${friendlyField}: ${msg}`
      })
      .filter((msg: string) => msg && msg.trim().length > 0)
      .join('. ') || 'Validation error. Please check your input.'
  } catch (e) {
    console.error('Error formatting validation errors:', e, errors)
    return 'Validation error. Please check your input.'
  }
}

